import React, { useState } from 'react';
import { Button, Box, Typography, CircularProgress, Alert } from '@mui/material';
import { Search as SearchIcon, Article as ArticleIcon } from '@mui/icons-material';
import { Contact, Initiative } from '../../types/database';

interface ManualNewsSearchButtonProps {
  contact?: Contact;
  initiative?: Initiative;
  onNewsFound?: (count: number) => void;
  disabled?: boolean;
}

export const ManualNewsSearchButton: React.FC<ManualNewsSearchButtonProps> = ({
  contact,
  initiative,
  onNewsFound,
  disabled = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSearchCount, setLastSearchCount] = useState<number | null>(null);

  const entity = contact || initiative;
  const entityType = contact ? 'contact' : 'initiative';

  const handleManualSearch = async () => {
    if (!entity?.use_api_data) {
      setError(`API data must be enabled for this ${entityType} to search for news`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the Netlify function to search for news
      const response = await fetch('/.netlify/functions/news-cron', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contactId: entity.id,
          manualSearch: true,
          entityType: entityType
        }),
      });

      if (!response.ok) {
        throw new Error(`News search failed: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.error) {
        const errorMessage = result.details ? `${result.error}: ${result.details}` : result.error;
        throw new Error(errorMessage);
      }

      setLastSearchCount(result.eventsCreated || 0);
      onNewsFound?.(result.eventsCreated || 0);
      
    } catch (err) {
      console.error('Error in manual news search:', err);
      setError(err instanceof Error ? err.message : 'Failed to search for news');
    } finally {
      setLoading(false);
    }
  };

  if (!entity?.use_api_data) {
    return (
      <Button
        variant="outlined"
        startIcon={<SearchIcon />}
        disabled
        sx={{
          minWidth: 140,
          textTransform: 'none',
          fontWeight: 500,
          opacity: 0.6,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1 }}>
            Search News
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1, opacity: 0.9 }}>
            Enable API Data
          </Typography>
        </Box>
      </Button>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Button
        variant="contained"
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
        onClick={handleManualSearch}
        disabled={disabled || loading}
        sx={{
          backgroundColor: 'info.main',
          color: 'white',
          '&:hover': {
            backgroundColor: 'info.dark',
          },
          '&:disabled': {
            backgroundColor: 'grey.400',
            color: 'grey.600',
          },
          minWidth: 140,
          textTransform: 'none',
          fontWeight: 500,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1 }}>
            Search News
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1, opacity: 0.9 }}>
            {loading ? 'Searching...' : 'Find Articles'}
          </Typography>
        </Box>
      </Button>

      {error && (
        <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
          {error}
        </Alert>
      )}

      {lastSearchCount !== null && !loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <ArticleIcon sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem' }}>
            Found {lastSearchCount} new article{lastSearchCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
